import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { AlertTriangle, CheckCircle2, Loader2, Upload, Users, XCircle } from "lucide-react";

import Modal from "../../../components/ui/Modal";
import { ApiService } from "../../../services/api";
import DepartmentSelector from "../../../components/selectors/DepartmentSelector";

interface BulkUserImportModalProps {
  isOpen: boolean;
  onClose: () => void;
  onCompleted?: () => void;
  initialDepartmentId?: string | null;
}

const MAX_TABLE_ROWS = 80;

const BulkUserImportModal: React.FC<BulkUserImportModalProps> = ({
  isOpen,
  onClose,
  onCompleted,
  initialDepartmentId = null,
}) => {
  const [selectedDepartmentId, setSelectedDepartmentId] = useState<string>(initialDepartmentId || "");
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [job, setJob] = useState<any>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [errorMessage, setErrorMessage] = useState<string>("");

  const pollTimerRef = useRef<ReturnType<typeof setInterval> | null>(null);

  const stopPolling = useCallback(() => {
    if (pollTimerRef.current) {
      clearInterval(pollTimerRef.current);
      pollTimerRef.current = null;
    }
  }, []);

  const refreshJobStatus = useCallback(
    async (jobId: string) => {
      try {
        const statusResponse = await ApiService.getUsersImportStatus(jobId);
        setJob(statusResponse);

        if (statusResponse?.status === "completed") {
          stopPolling();
          onCompleted?.();
        } else if (statusResponse?.status === "failed") {
          stopPolling();
          setErrorMessage(statusResponse?.error || "Toplu ekleme sırasında bir hata oluştu.");
        }
      } catch (error: any) {
        stopPolling();
        setErrorMessage(error?.message || "Yükleme durumu alınamadı.");
      }
    },
    [onCompleted, stopPolling]
  );

  const startPolling = useCallback(
    (jobId: string) => {
      stopPolling();
      pollTimerRef.current = setInterval(() => {
        refreshJobStatus(jobId);
      }, 1500);
    },
    [refreshJobStatus, stopPolling]
  );

  useEffect(() => {
    if (!isOpen) {
      stopPolling();
      return;
    }
  }, [isOpen, stopPolling]);

  useEffect(() => {
    if (!isOpen) return;
    if (!initialDepartmentId) return;
    setSelectedDepartmentId(initialDepartmentId);
  }, [initialDepartmentId, isOpen]);

  useEffect(() => {
    return () => {
      stopPolling();
    };
  }, [stopPolling]);

  const handleStartImport = async () => {
    if (!selectedDepartmentId) {
      setErrorMessage("Lütfen bir departman seçin.");
      return;
    }
    if (!selectedFile) {
      setErrorMessage("Lütfen bir Excel dosyası seçin.");
      return;
    }

    setErrorMessage("");
    setIsSubmitting(true);

    try {
      const response = await ApiService.startUsersImportFromExcel(selectedDepartmentId, selectedFile);
      setJob(response);

      if (response?.status === "queued" || response?.status === "processing") {
        startPolling(response.id);
      }
    } catch (error: any) {
      setErrorMessage(error?.message || "Toplu ekleme başlatılamadı.");
    } finally {
      setIsSubmitting(false);
    }
  };

  const summary = job?.summary || {};
  const progress = job?.progress || {};
  const results = job?.results || {};

  const limitedMissingRows = useMemo(
    () => (results?.missingRequired || []).slice(0, MAX_TABLE_ROWS),
    [results]
  );
  const limitedExistingRows = useMemo(
    () => (results?.skippedExisting || []).slice(0, MAX_TABLE_ROWS),
    [results]
  );

  const renderTable = (
    rows: any[],
    columns: Array<{ key: string; title: string }>,
    emptyText: string,
    totalCount?: number
  ) => {
    if (!rows || rows.length === 0) {
      return <p className="text-sm text-gray-500">{emptyText}</p>;
    }

    return (
      <div className="space-y-2">
        <div className="text-xs text-gray-500">
          Toplam {totalCount ?? rows.length} kayıt
          {(totalCount || 0) > rows.length ? ` (ilk ${rows.length} satır gösteriliyor)` : ""}
        </div>
        <div className="max-h-52 overflow-auto rounded-lg border border-gray-200">
          <table className="w-full min-w-[560px] text-xs">
            <thead className="bg-gray-50 text-gray-600 sticky top-0">
              <tr>
                {columns.map((column) => (
                  <th key={column.key} className="px-2 py-2 text-left font-semibold">
                    {column.title}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {rows.map((row, index) => (
                <tr key={`${row.rowNumber || "row"}-${index}`} className="hover:bg-gray-50">
                  {columns.map((column) => (
                    <td key={column.key} className="px-2 py-1.5 align-top text-gray-700">
                      {row?.[column.key] ?? "-"}
                    </td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    );
  };

  const statusLabel = (() => {
    if (!job) return "Hazır";
    if (job.status === "queued") return "Kuyrukta";
    if (job.status === "processing") return "Yükleniyor";
    if (job.status === "completed") return "Tamamlandı";
    if (job.status === "failed") return "Hata";
    return "Hazır";
  })();

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title="Toplu Ekle"
      size="lg"
      panelClassName="mx-3 max-h-[92vh] rounded-xl sm:mx-4 sm:max-h-[92vh]"
      bodyClassName="space-y-4"
      overflowVisible>
      <div className="space-y-4">
        <div className="rounded-xl border border-gray-200 bg-white p-4 sm:p-5">
          <div className="mb-4">
            <h3 className="text-sm font-semibold text-gray-900">Dosya ve Departman Seçimi</h3>
            <p className="mt-1 text-xs text-gray-500">Excel dosyasını ve kullanıcıların ekleneceği departmanı seçin.</p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
            <div className="md:col-span-2">
              <label className="block text-xs font-medium text-gray-700 mb-1.5">Departman</label>
              <DepartmentSelector
                value={selectedDepartmentId}
                onChange={(departmentId) => setSelectedDepartmentId(departmentId || "")}
                placeholder="Departman seçin"
                className="w-full"
                disabled={isSubmitting}
                required
              />
            </div>

            <div>
              <label className="block text-xs font-medium text-gray-700 mb-1.5">Excel Dosyası</label>
              <input
                type="file"
                accept=".xlsx,.xls"
                className="w-full min-w-0 rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm text-gray-700 file:mr-3 file:rounded-md file:border-0 file:bg-gray-100 file:px-2.5 file:py-1.5 file:text-xs file:font-medium file:text-gray-700 hover:file:bg-gray-200"
                onChange={(event) => setSelectedFile(event.target.files?.[0] || null)}
                disabled={isSubmitting}
              />
            </div>
          </div>

          <div className="mt-4 flex flex-col sm:flex-row sm:flex-wrap sm:items-center gap-2">
            <button
              type="button"
              onClick={handleStartImport}
              disabled={isSubmitting || !selectedFile || !selectedDepartmentId}
              className="inline-flex w-full sm:w-auto items-center justify-center gap-2 rounded-lg bg-blue-600 px-4 py-2 text-sm font-medium text-white transition hover:bg-blue-700 disabled:cursor-not-allowed disabled:opacity-50">
              {isSubmitting ? <Loader2 className="w-4 h-4 animate-spin" /> : <Upload className="w-4 h-4" />}
              Toplu Ekle
            </button>
            <span
              className={`inline-flex w-fit items-center gap-1 px-2.5 py-1 rounded-full text-xs font-medium ${
                job?.status === "completed"
                  ? "bg-green-100 text-green-700"
                  : job?.status === "failed"
                    ? "bg-red-100 text-red-700"
                    : "bg-gray-100 text-gray-700"
              }`}>
              {job?.status === "processing" && <Loader2 className="w-3 h-3 animate-spin" />}
              {statusLabel}
            </span>
            {job?.message && <span className="text-sm text-gray-600 break-words">{job.message}</span>}
          </div>
        </div>

        {errorMessage && (
          <div className="flex items-start gap-2 rounded-lg border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-700">
            <AlertTriangle className="w-4 h-4 mt-0.5 flex-shrink-0" />
            <span>{errorMessage}</span>
          </div>
        )}

        {job && (
          <div className="space-y-4 rounded-xl border border-gray-200 bg-white p-4 sm:p-5">
            <div>
              <div className="mb-1 flex items-center justify-between text-xs text-gray-600">
                <span>
                  İlerleme: {progress.processedRows || 0}/{progress.totalRows || 0}
                </span>
                <span>%{progress.percentage || 0}</span>
              </div>
              <div className="h-2 overflow-hidden rounded-full bg-gray-200">
                <div className="h-full bg-blue-600 transition-all duration-300" style={{ width: `${progress.percentage || 0}%` }} />
              </div>
            </div>

            <div className="grid grid-cols-2 lg:grid-cols-4 gap-2.5">
              <div className="rounded-lg border border-gray-200 bg-gray-50 p-3">
                <div className="text-xs text-gray-600">Oluşturulan</div>
                <div className="mt-1 text-xl font-semibold text-gray-900">{summary.created || 0}</div>
              </div>
              <div className="rounded-lg border border-gray-200 bg-gray-50 p-3">
                <div className="text-xs text-gray-600">Sistemde Mevcut</div>
                <div className="mt-1 text-xl font-semibold text-gray-900">{summary.skippedExisting || 0}</div>
              </div>
              <div className="rounded-lg border border-gray-200 bg-gray-50 p-3">
                <div className="text-xs text-gray-600">Eksik / Geçersiz</div>
                <div className="mt-1 text-xl font-semibold text-gray-900">{summary.skippedMissingRequired || 0}</div>
              </div>
              <div className="rounded-lg border border-gray-200 bg-gray-50 p-3">
                <div className="text-xs text-gray-600">Dosya İçi Tekrar</div>
                <div className="mt-1 text-xl font-semibold text-gray-900">{summary.skippedDuplicateInFile || 0}</div>
              </div>
            </div>

            <div className="grid grid-cols-1 xl:grid-cols-2 gap-4">
              <div className="rounded-lg border border-gray-200 p-3 bg-white">
                <div className="flex items-center gap-2 text-sm font-semibold text-gray-800 mb-2">
                  <XCircle className="w-4 h-4 text-red-500" />
                  Eksik Bilgiler (Eklenemedi)
                </div>
                {renderTable(
                  limitedMissingRows,
                  [
                    { key: "rowNumber", title: "Satır" },
                    { key: "fullName", title: "Ad Soyad" },
                    { key: "email", title: "E-Posta" },
                    { key: "tcNumber", title: "TC" },
                    { key: "message", title: "Neden" },
                  ],
                  "Eksik kayıt yok.",
                  results?.missingRequired?.length
                )}
              </div>

              <div className="rounded-lg border border-gray-200 p-3 bg-white">
                <div className="flex items-center gap-2 text-sm font-semibold text-gray-800 mb-2">
                  <Users className="w-4 h-4 text-amber-500" />
                  Sistemde Mevcut Olanlar (Atlandı)
                </div>
                {renderTable(
                  limitedExistingRows,
                  [
                    { key: "rowNumber", title: "Satır" },
                    { key: "fullName", title: "Ad Soyad" },
                    { key: "email", title: "E-Posta" },
                    { key: "tcNumber", title: "TC" },
                    { key: "existingDepartmentName", title: "Departman" },
                    { key: "message", title: "Not" },
                  ],
                  "Sistemde mevcut kayıt yok.",
                  results?.skippedExisting?.length
                )}
              </div>
            </div>

            {(results?.failedToCreate?.length || 0) > 0 && (
              <div className="rounded-lg border border-red-200 bg-red-50 p-3">
                <div className="flex items-center gap-2 text-sm font-semibold text-red-800 mb-2">
                  <AlertTriangle className="w-4 h-4" />
                  Hata Nedeniyle Eklenemeyenler ({results.failedToCreate.length})
                </div>
                {renderTable(
                  (results.failedToCreate || []).slice(0, MAX_TABLE_ROWS),
                  [
                    { key: "rowNumber", title: "Satır" },
                    { key: "fullName", title: "Ad Soyad" },
                    { key: "email", title: "E-Posta" },
                    { key: "tcNumber", title: "TC" },
                    { key: "message", title: "Hata" },
                  ],
                  "Hata yok.",
                  results.failedToCreate.length
                )}
              </div>
            )}

            {job?.status === "completed" && (
              <div className="flex items-center gap-2 rounded-lg border border-green-200 bg-green-50 px-3 py-2 text-sm text-green-700">
                <CheckCircle2 className="w-4 h-4" />
                Toplu ekleme tamamlandı.
              </div>
            )}
          </div>
        )}
      </div>
    </Modal>
  );
};

export default BulkUserImportModal;
